import { useMutation } from '@tanstack/react-query';
import type { Provider } from '@supabase/supabase-js';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  GoogleIcon,
  SteamIcon,
  GitHubIcon,
} from '@/components/icons/CompanyIcons';

type IconType = typeof GoogleIcon;

// Button order is intentional; Steam is OpenID 2.0 (custom flow), the rest are
// native Supabase OAuth providers.
type AuthButton =
  | { kind: 'oauth'; provider: Provider; label: string; Icon: IconType }
  | { kind: 'steam'; label: string; Icon: IconType };

// Apple/Facebook intentionally excluded — Apple pending dev enrollment, Facebook dropped.
const BUTTONS: AuthButton[] = [
  {
    kind: 'oauth',
    provider: 'google',
    label: 'Continue with Google',
    Icon: GoogleIcon,
  },
  { kind: 'steam', label: 'Continue with Steam', Icon: SteamIcon },
  {
    kind: 'oauth',
    provider: 'github',
    label: 'Continue with GitHub',
    Icon: GitHubIcon,
  },
];

/**
 * SSO sign-in buttons. The OAuth providers (Google, Apple, GitHub, Facebook)
 * each need to be enabled in Supabase Auth (dashboard for staging/prod,
 * config.toml for local) with valid client credentials. Steam uses a custom
 * OpenID flow that is wired separately.
 */
export function SocialAuthButtons({
  redirectPath = '/',
}: {
  redirectPath?: string;
}) {
  const { toast } = useToast();
  const { user } = useAuth();

  const { mutate, isPending, variables } = useMutation({
    mutationFn: async (provider: Provider) => {
      const redirectTo =
        redirectPath !== '/'
          ? `${window.location.origin}${redirectPath}`
          : `${window.location.origin}/`;

      // A guest already has an anonymous session: LINK the OAuth identity to it
      // (same user_id) so their work carries over, instead of signing into a
      // fresh account. If manual linking is unavailable or the identity is
      // already taken, fall back to a normal OAuth sign-in so auth never breaks.
      if (user?.is_anonymous) {
        const { error: linkError } = await supabase.auth.linkIdentity({
          provider,
          options: { redirectTo },
        });
        if (!linkError) return;
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo },
      });
      if (error) throw error;
    },
    onError: (error) => {
      toast({
        title: 'Whoopsies',
        description:
          error instanceof Error ? error.message : 'Something went wrong',
        variant: 'destructive',
      });
    },
  });

  const handleSteam = () => {
    // Steam isn't a Supabase provider — start the custom OpenID flow handled by
    // the steam-auth edge function, which redirects back with a session.
    const base = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    if (!base) {
      toast({
        title: 'Whoopsies',
        description: 'Steam sign-in is not configured.',
        variant: 'destructive',
      });
      return;
    }
    const params = new URLSearchParams({
      app: window.location.origin,
      redirect: redirectPath,
    });
    window.location.href = `${base}/functions/v1/steam-auth?${params.toString()}`;
  };

  return (
    <div className="flex flex-col gap-2">
      {BUTTONS.map((btn) => {
        const pending =
          btn.kind === 'oauth' && isPending && variables === btn.provider;
        return (
          <Button
            key={btn.kind === 'oauth' ? btn.provider : 'steam'}
            type="button"
            onClick={() =>
              btn.kind === 'oauth' ? mutate(btn.provider) : handleSteam()
            }
            disabled={btn.kind === 'oauth' && isPending}
            className="flex w-full items-center gap-2 hover:bg-telekinesis-gold/10"
          >
            <btn.Icon className="w-4" />
            <span>{pending ? 'Redirecting…' : btn.label}</span>
          </Button>
        );
      })}
    </div>
  );
}
